import React, { useState, useEffect } from "react";
import { DatePicker, Select, Button, Table, Tag, Alert, message } from "antd";
import dayjs from "dayjs";
import {
  useFetchActivityTimeline,
  useFetchEmployeeOptions,
} from "../hooks/useFetch";

const { RangePicker } = DatePicker;

// สี/ชื่อของแต่ละเหตุการณ์ — ให้ "หายไปจากระบบ" เป็นโทนร้อน จะได้กวาดตาเจอเร็ว
const EVENT_META = {
  clock_in: { label: "ตอกเข้างาน", color: "green" },
  clock_out: { label: "ตอกออกงาน", color: "default" },
  online: { label: "ออนไลน์", color: "blue" },
  offline: { label: "ออฟไลน์", color: "volcano" },
  task_on: { label: "เปิดหน้าที่", color: "cyan" },
  task_off: { label: "ปิดหน้าที่", color: "red" },
};

const formatMinutes = (m) => {
  if (m == null) return "-";
  if (m < 60) return `${m} นาที`;
  return `${Math.floor(m / 60)} ชม. ${m % 60} นาที`;
};

// รายงาน log — ไทม์ไลน์รวมของ "การเปิด/ปิดหน้าที่" กับ "ออนไลน์-ออฟไลน์/ตอกบัตร"
// ทั้งสองอย่างมีผลกับการจ่ายเคสพอ ๆ กัน แต่เดิมดูแยกกันคนละที่ ไล่เหตุการณ์ไม่ได้
const ActivityLogPage = () => {
  const [range, setRange] = useState([dayjs(), dayjs()]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [rows, setRows] = useState([]);
  const [taskLogAvailable, setTaskLogAvailable] = useState(true);

  const { data: employeeOptions = [], isLoading: isLoadingEmployees } =
    useFetchEmployeeOptions();
  const { mutateAsync: fetchTimeline, isLoading } = useFetchActivityTimeline();

  const handleSearch = async (r = range, userId = selectedUserId) => {
    if (!r || !r[0] || !r[1]) return;
    try {
      const { result } = await fetchTimeline({
        startDate: r[0].format("YYYY-MM-DD"),
        endDate: r[1].format("YYYY-MM-DD"),
        userId: userId || undefined,
      });
      setRows(result?.events || []);
      setTaskLogAvailable(result?.taskLogAvailable !== false);
    } catch (error) {
      message.error(
        error?.response?.data?.message || "ดึงรายงาน log ไม่สำเร็จ"
      );
    }
  };

  // โหลดครั้งแรก = วันนี้
  useEffect(() => {
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      title: "เวลา",
      dataIndex: "eventTime",
      key: "eventTime",
      width: 170,
      render: (v) => (
        <span>
          <span style={{ color: "#999" }}>{dayjs(v).format("DD/MM")}</span>{" "}
          <strong>{v.slice(11, 19)}</strong>
        </span>
      ),
    },
    {
      title: "พนักงาน",
      dataIndex: "userName",
      key: "userName",
      width: 160,
      render: (v) => <span style={{ fontWeight: 600 }}>{v}</span>,
    },
    {
      title: "เหตุการณ์",
      dataIndex: "type",
      key: "type",
      width: 130,
      filters: Object.entries(EVENT_META).map(([value, m]) => ({
        text: m.label,
        value,
      })),
      onFilter: (value, r) => r.type === value,
      render: (t) => {
        const meta = EVENT_META[t] || { label: t, color: "default" };
        return <Tag color={meta.color}>{meta.label}</Tag>;
      },
    },
    {
      title: "รายละเอียด",
      key: "detail",
      render: (_, r) => {
        if (r.type === "task_on" || r.type === "task_off") {
          return (
            <span>
              <Tag color={r.type === "task_on" ? "cyan" : "red"}>{r.job}</Tag>
              <span style={{ color: "#999" }}>{r.beforeValue || "(ว่าง)"}</span>
              {" → "}
              <span style={{ fontWeight: 600 }}>{r.afterValue || "(ว่าง)"}</span>
            </span>
          );
        }
        if (r.type === "offline") {
          return r.ongoing ? (
            <span style={{ color: "#d4380d", fontWeight: 600 }}>
              ยังออฟไลน์อยู่ (ไม่ได้รับเคส)
            </span>
          ) : (
            <span>หายไป {formatMinutes(r.awayMinutes)}</span>
          );
        }
        if (r.type === "online" && r.awayMinutes != null) {
          return <span>กลับมาหลังหายไป {formatMinutes(r.awayMinutes)}</span>;
        }
        return <span style={{ color: "#bfbfbf" }}>—</span>;
      },
    },
    {
      title: "โดย",
      dataIndex: "changedByName",
      key: "changedByName",
      width: 140,
      render: (v) => v || <span style={{ color: "#bfbfbf" }}>—</span>,
    },
  ];

  return (
    // App.css ตั้ง body { color: white } ไว้ทั้งแอป ต้องกำหนดสีเองไม่งั้นตัวหนังสือหาย
    <div style={{ padding: 24, color: "#212121" }}>
      <div style={{ marginBottom: 16 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700, margin: 0, color: "#212121" }}>รายงาน log</h1>
        <p style={{ margin: "8px 0 0", color: "#666" }}>
          ไทม์ไลน์รวม: ใครเปิด/ปิดหน้าที่ให้ใคร เทียบกับช่วงที่ออฟไลน์ (พัก) และการตอกบัตร
          — ใช้ไล่ว่าทำไมเคสถึงไม่มีคนรับในช่วงเวลาหนึ่ง
        </p>
      </div>

      {!taskLogAvailable && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 16 }}
          message="ยังไม่มีตาราง UserTaskTypeLog"
          description="ตอนนี้แสดงได้เฉพาะการตอกบัตร/ออนไลน์-ออฟไลน์ ต้องสร้างตารางก่อนถึงจะเห็นประวัติการเปิด/ปิดหน้าที่"
        />
      )}

      <div style={{ background: "#fff", borderRadius: 10, padding: 20, marginBottom: 16 }}>
        <div className="flex gap-3 items-center flex-wrap">
          <RangePicker
            value={range}
            onChange={(r) => setRange(r)}
            format="DD/MM/YYYY"
            allowClear={false}
          />
          <Select
            className="w-64"
            placeholder="พนักงานทั้งหมด"
            allowClear
            showSearch
            optionFilterProp="label"
            loading={isLoadingEmployees}
            options={employeeOptions}
            value={selectedUserId}
            onChange={(v) => setSelectedUserId(v || null)}
          />
          <Button type="primary" loading={isLoading} onClick={() => handleSearch()}>
            ค้นหา
          </Button>
        </div>
      </div>

      <div style={{ background: "#fff", borderRadius: 10, padding: 16 }}>
        <Table
          rowKey={(r) => `${r.eventTime}-${r.userId}-${r.type}-${r.job || ""}`}
          loading={isLoading}
          dataSource={rows}
          columns={columns}
          bordered
          size="small"
          scroll={{ x: "max-content" }}
          rowClassName={(r) =>
            r.type === "task_off" || r.ongoing ? "bg-red-50" : ""
          }
          pagination={{
            pageSize: 50,
            showSizeChanger: false,
            showTotal: (t) => `ทั้งหมด ${t} เหตุการณ์`,
          }}
        />
      </div>
      <style>{`.ant-table-tbody > tr:hover > td { background-color: #e8f4f8 !important; }`}</style>
    </div>
  );
};

export default ActivityLogPage;
